import classNames from 'classnames'
import IconRender from 'components/common/IconRender'
import { isMobile } from 'components/utils'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'

import QuantityBubble from './QuantityBubble'

type DropdownProps = {
  icon: string
  label: string
  options: { label: string; value: string | number }[]
  value: string[]
  onChange: (value: string[]) => void
  onFilter?: () => void
  multiSelect?: boolean
}

const FilterDropdown = ({
  icon,
  label,
  options,
  value,
  onChange,
  onFilter,
  multiSelect = false,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [selecteds, setSelecteds] = useState<string[]>(value)
  const { t } = useTranslation();
  const mobile = isMobile()

  useEffect(() => {
    setSelecteds(value)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: string) => {
    const next = multiSelect
      ? selecteds.includes(option)
        ? selecteds.filter(item => item !== option)
        : [...selecteds, option]
      : [option]

    setSelecteds(next)
    onChange(next)
    onFilter?.()

    if (!multiSelect) {
      setIsOpen(false)
    }
  }

  const filterOptions = () => {
    onChange(selecteds)
    onFilter?.()
    setIsOpen(false)
  }

  const clearOptions = () => {
    setSelecteds([])
    onChange([])
    onFilter?.()
    setIsOpen(false)
  }

  return (
    <div
      className="relative text-sm w-full sm:w-fit"
      ref={dropdownRef}
      style={{ zIndex: 50 }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={classNames('w-full flex items-center relative pl-10 pr-8', {
          'h-10 rounded-lg': !mobile,
          'h-12': mobile,
          'border border-imovPrimary': isOpen && !mobile,
          'border border-gray-300': !isOpen && !mobile,
          'border-b border-gray-300': !isOpen && mobile,
        })}
      >
        <span className="absolute left-3 inset-y-0 flex items-center text-gray-500">
          <IconRender
            icon={icon}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        {label && (
          <span className="text-imovPrimary text-md font-semibold whitespace-nowrap px-2">{t(label)}</span>
        )}
        <span className={classNames("absolute inset-y-0 flex items-center text-gray-500", {
          "right-2": !mobile,
          "right-10": mobile,
        })}>
          <IconRender
            icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        <QuantityBubble selecteds={selecteds.length} />
      </button>

      {(isOpen || mobile) && buttonRef.current && (
        <div
          className={classNames(
            "z-50 bg-white border border-gray-200 text-sm transition-all duration-300 ease-in-out",
            {
              "fixed rounded-md shadow-lg": !mobile,
              "relative": mobile,
              "opacity-0 max-h-0 overflow-hidden": mobile && !isOpen,
              "opacity-100 max-h-96 overflow-visible": mobile && isOpen,
            }
          )}
          style={!mobile ? {
            top: 50,
            minWidth: buttonRef.current.getBoundingClientRect().width,
            width: 'max-content',
          } : {}}
        >
          {!mobile && selecteds && selecteds.length > 0 && (
            <div className="flex gap-2 p-2 shadow-sm">
              <button onClick={() => filterOptions()} className="flex-1 px-3 py-2 bg-imovPrimary text-white rounded cursor-pointer hover:bg-imovPrimaryLight transition-colors">
                {t('common.filter')}
              </button>
              <button onClick={() => clearOptions()} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded cursor-pointer hover:bg-gray-300 transition-colors">
                {t('common.clear')}
              </button>
            </div>
          )}
          <div>
            {options.map((opt, index) => {
              const isSelected = selecteds.includes(opt.value.toString())
              return (
                <div
                  key={`${opt.value}-${index}`}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-100 flex items-center justify-between ${isSelected ? 'bg-imovPrimary bg-opacity-5 border-l-2 border-imovPrimary' : 'border-l-2 border-gray-200'
                    }`}
                  onClick={() => handleSelect(opt.value.toString())}
                >
                <span className={isSelected ? 'text-imovPrimary font-medium' : ''}>
                  {t(opt.label)}
                </span>
                {isSelected && mobile && (
                  <IconRender
                    icon="regular-check"
                    size={IconSizes.SM}
                    color={CustomColorsRef.Primary}
                  />
                )}
              </div>
            )
          })}
          </div>
        </div>
      )}
    </div>
  )
}

export default FilterDropdown
